import type { OrderSettlementDto } from '../../../dto/orderSettlement';
import type { OrderCostPlan, OrderWarehouseSummary } from '../../../types/orderWarehouseSummary';
import type { OrderMarginStage } from './orderMarginStage';

/**
 * Tiles of the „Faktura” step: revenue − Σ costs = margin, always from ONE
 * backend source (owner priority: the math must add up and be explained).
 *
 * - FINAL: the invoice ledger `settlement.actual` — revenue, one total cost
 *   („Koszt z rozliczenia”, the ledger has no split) and margin. Never the
 *   ledger revenue with live-plan costs.
 * - Otherwise the WP5 plan of the warehouse summary (full-margin readers):
 *   cost from WZ (`issuedCostPLN`), planned cost of the part not issued yet
 *   (stock FIFO + average purchase cost), outgoing transport, other order
 *   costs and — when present — remaining assigned costs. Their sum is the
 *   plan's `knownCostPLN`, so revenue − Σ = `marginPLN`.
 * - No plan (Handlowiec, Operacje): null — the frontend computes no cost.
 */
export type OrderMarginCostKey = 'wzCost' | 'plannedCost' | 'transport' | 'other' | 'remaining' | 'settlementCost';

export interface OrderMarginBreakdown {
  source: 'PLAN' | 'SETTLEMENT';
  revenue: number | null;
  costs: Array<{ key: OrderMarginCostKey; amount: number }>;
  /** null when part of the cost is unknown (never a fabricated number). */
  margin: number | null;
  percent: number | null;
  /** Some cost is unknown: the costs listed are only the known part. */
  costPending: boolean;
}

const money = (value: number) => Math.round(value * 100) / 100;

export function getOrderMarginBreakdown(input: {
  stage: OrderMarginStage;
  settlement: Pick<OrderSettlementDto, 'actual'> | null;
  finance: OrderWarehouseSummary['finance'] | null | undefined;
}): OrderMarginBreakdown | null {
  const { stage, settlement, finance } = input;
  if (stage === 'FINAL' && settlement) {
    const { actual } = settlement;
    return {
      source: 'SETTLEMENT',
      revenue: actual.revenue,
      costs: actual.totalCost == null ? [] : [{ key: 'settlementCost', amount: actual.totalCost }],
      margin: actual.margin,
      percent: actual.margin == null ? null : actual.marginPercent,
      costPending: actual.totalCost == null,
    };
  }
  const plan: OrderCostPlan | undefined = finance?.plan;
  if (!plan || !finance) return null;
  const planned = money(plan.stockCostPLN + plan.averageCostPLN);
  const remaining = money(plan.manualCostPLN - finance.transportCostPLN - finance.otherCostPLN);
  const costs: OrderMarginBreakdown['costs'] = [{ key: 'wzCost', amount: plan.issuedCostPLN }];
  if (planned > 0.004) costs.push({ key: 'plannedCost', amount: planned });
  costs.push({ key: 'transport', amount: finance.transportCostPLN });
  costs.push({ key: 'other', amount: finance.otherCostPLN });
  if (Math.abs(remaining) > 0.004) costs.push({ key: 'remaining', amount: remaining });
  return {
    source: 'PLAN',
    revenue: plan.revenuePLN,
    costs,
    margin: plan.marginPLN,
    percent: plan.marginPercent,
    costPending: plan.costPending,
  };
}
